/**
 * AdminSidebar · v2.6.9 batch BJ
 * ===============================
 * Left accordion navigation rail for the Admin Console. Replaces the previous
 * wide horizontal `<TabsList>`. Each category is a row; categories that own
 * sub-tabs (Driver Mgmt, Administration) expand inline with a chevron toggle.
 *
 * Mobile: collapses to a hamburger button in a fixed top-left position that
 * opens the same nav as a slide-in drawer (`<Sheet>`).
 *
 * Sub-tab coordination: clicking a sub-item updates the URL hash to
 *   `#sub=<value>`
 * The existing `DriverManagementGroup` and `AdministrationGroup` components
 * subscribe to `hashchange` and switch their internal `<Tabs value=…>` to
 * match. No refactor of those components beyond a tiny `useHashSubTab` hook.
 */
import { Fragment, useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import CurrentlyOnlineWidget from "@/components/admin/CurrentlyOnlineWidget";
import VersionBadge from "@/components/VersionBadge";
import SidebarProfileFooter from "@/components/admin/SidebarProfileFooter";
import { buildAdminNavTree } from "@/components/admin/adminNavTree";
import {
  ChevronDown, ChevronRight, Menu, Pin,
} from "lucide-react";

// Helper hook · read+write `#sub=<value>` so any component nested inside a
// `<Tabs value=…>` can stay in sync with the sidebar's selection.
export function useHashSubTab(initial) {
  const readHash = useCallback(() => {
    const h = (window.location.hash || "").replace(/^#/, "");
    const params = new URLSearchParams(h);
    return params.get("sub");
  }, []);
  const [sub, setSub] = useState(() => readHash() || initial);
  useEffect(() => {
    const onHash = () => {
      const v = readHash();
      if (v) setSub(v);
    };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, [readHash]);
  // Also push the sub value into the hash when the component changes it via
  // its own <TabsList>, so the sidebar's "active" highlight stays accurate.
  const setSubAndHash = useCallback((v) => {
    setSub(v);
    const h = new URLSearchParams((window.location.hash || "").replace(/^#/, ""));
    h.set("sub", v);
    if (window.location.hash !== `#${h.toString()}`) {
      window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}#${h.toString()}`);
    }
  }, []);
  return [sub, setSubAndHash];
}

// ---- Category definitions ----------------------------------------------------
// v2.7.20 · Mobile drawer now consumes the SAME `buildAdminNavTree` as the
// desktop top nav (see /app/frontend/src/components/admin/adminNavTree.js).
// Result: mobile mirrors desktop 1:1 — same Dispatch / Fleet / Developer
// dropdowns, same visibility gates, same accent colors. To restructure the
// nav, edit `adminNavTree.js` only.
const buildNavTree = buildAdminNavTree;

// v2.7.65+E140 · Admin Console redesign — clean white nav rows with
// translucent hover/active overlays. Kept the `accent` param so icons can
// carry a subtle role-hint colour while the row itself stays neutral.
const ICON_ACCENT = {
  red:     "text-red-300",
  rose:    "text-rose-300",
  amber:   "text-amber-300",
  indigo:  "text-indigo-300",
  violet:  "text-violet-300",
  sky:     "text-sky-300",
  cyan:    "text-yellow-300",
  fuchsia: "text-fuchsia-300",
  emerald: "text-emerald-300",
};
const iconTint = (accent) => ICON_ACCENT[accent] || "text-white/70";
const rowClass = (isActive) =>
  `flex items-center rounded-md text-sm font-medium transition-colors ${
    isActive
      ? "bg-white/20 text-white"
      : "text-white hover:bg-white/[0.12]"
  }`;
const subRowClass = (isActive) =>
  `w-full flex items-center gap-2 px-3 py-1.5 rounded-md text-xs transition-colors ${
    isActive
      ? "bg-white/20 text-white font-semibold"
      : "text-white/85 hover:bg-white/[0.10]"
  }`;

function NavRow({ item, activeTab, activeSub, onTopNav, expanded, toggleExpanded, onClose, unreadBadges, pinned, onTogglePin, hidePinControl }) {
  const { value, label, Icon, accent, children, badge, targetTab, targetSub } = item;
  const navigate = useNavigate();
  const isActiveTop = activeTab === (targetTab || value);
  const hasChildren = !!children?.length;
  const badgeCount = badge && unreadBadges ? unreadBadges[badge] || 0 : 0;
  // v2.6.9 batch BJ-5 · clicking the parent ALWAYS expands (no toggle to
  // collapse) so the sub-items stay reachable. The dedicated chevron button
  // beside it handles explicit collapse.
  const onMainClick = () => {
    // v2.7.0 batch DM · Clicking "Developer" parent → route to the admin tab
    // (which hosts all 5 developer sub-pages) with the first sub selected.
    // Same trick as Accounting/Branding parents.
    if (value === "developer" && hasChildren && !expanded) {
      onTopNav("admin");
      const h = new URLSearchParams((window.location.hash || "").replace(/^#/, ""));
      h.set("sub", "developer-portal");
      window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}#${h.toString()}`);
      window.dispatchEvent(new HashChangeEvent("hashchange"));
      toggleExpanded(value, true);
      if (onClose) onClose();
      return;
    }
    // v2.7.0 batch DO++++ · FEATURE 1 · Flat top-level items can declare
    // `targetTab` + `targetSub` to route to a sibling tab's sub-panel
    // without expanding any local children (Book Ride / Completed Rides).
    if (targetTab) {
      onTopNav(targetTab);
      if (targetSub) {
        const h = new URLSearchParams((window.location.hash || "").replace(/^#/, ""));
        h.set("sub", targetSub);
        window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}#${h.toString()}`);
        window.dispatchEvent(new HashChangeEvent("hashchange"));
      }
      if (onClose) onClose();
      return;
    }
    onTopNav(value);
    if (hasChildren && !expanded) toggleExpanded(value, true);
    if (onClose && !hasChildren) onClose();
  };
  return (
    <li>
      <div className={`relative ${rowClass(isActiveTop)}`}>
        {/* v2.7.65+E140 · Amber left-edge indicator for active route */}
        {isActiveTop && (
          <span aria-hidden className="absolute left-0 top-1.5 bottom-1.5 w-1 rounded-r-full bg-amber-400"/>
        )}
        <button
          type="button"
          data-testid={`sidebar-${value}`}
          onClick={onMainClick}
          className="flex-1 flex items-center gap-2 px-3 py-2.5 text-left"
        >
          <Icon className={`h-4 w-4 flex-shrink-0 ${isActiveTop ? "text-amber-300" : iconTint(accent)}`}/>
          <span className="flex-1">{label}</span>
          {badgeCount > 0 && (
            <span
              data-testid={`sidebar-${value}-badge`}
              className="ml-auto px-1.5 py-0.5 rounded-full bg-red-500 text-white text-[10px] font-bold leading-none min-w-[18px] text-center animate-pulse"
            >{badgeCount > 99 ? "99+" : badgeCount}</span>
          )}
        </button>
        {hasChildren && (
          <button
            type="button"
            data-testid={`sidebar-${value}-toggle`}
            aria-label={expanded ? "Collapse" : "Expand"}
            onClick={(e) => { e.stopPropagation(); toggleExpanded(value); }}
            className="px-2 py-2.5 text-white/50 hover:text-white"
          >
            {expanded ? <ChevronDown className="h-3.5 w-3.5"/> : <ChevronRight className="h-3.5 w-3.5"/>}
          </button>
        )}
        {/* v2.7.0 batch DO+++++ · Pin-to-top control */}
        {!hidePinControl && onTogglePin && (
          <button
            type="button"
            data-testid={`sidebar-${value}-pin`}
            aria-label={pinned ? "Unpin from top" : "Pin to top"}
            title={pinned ? "Unpin from top" : "Pin to top"}
            onClick={(e) => { e.stopPropagation(); onTogglePin(value); }}
            className={`px-1.5 py-2.5 ${pinned ? "text-amber-400" : "text-white/25 hover:text-amber-300"}`}
          >
            {pinned ? <Pin className="h-3 w-3 fill-amber-400"/> : <Pin className="h-3 w-3"/>}
          </button>
        )}
      </div>
      {hasChildren && expanded && (
        <ul className="mt-1 ml-4 pl-3 border-l border-white/10 space-y-0.5" data-testid={`sidebar-${value}-sub`}>
          {children.map((c) => {
            const SubIcon = c.Icon;
            const isActiveSub = isActiveTop && activeSub === c.sub;
            return (
              <li key={c.sub}>
                <button
                  type="button"
                  data-testid={`sidebar-${value}-${c.sub}`}
                  onClick={() => {
                    // v2.6.9 batch BT · sub-items with an `href` route to a
                    // dedicated page instead of toggling the hash sub-tab.
                    if (c.href) {
                      navigate(c.href);
                      if (onClose) onClose();
                      return;
                    }
                    // v2.6.9 batch BJ-5 · cross-parent shortcut support — sub-items
                    // can declare a `targetTab` to navigate to a DIFFERENT top-level
                    // panel (e.g. Overview → Book Ride lands on Driver Mgmt).
                    const tab = c.targetTab || value;
                    onTopNav(tab);
                    const h = new URLSearchParams((window.location.hash || "").replace(/^#/, ""));
                    h.set("sub", c.sub);
                    window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}#${h.toString()}`);
                    window.dispatchEvent(new HashChangeEvent("hashchange"));
                    if (onClose) onClose();
                  }}
                  className={subRowClass(isActiveSub)}
                >
                  {SubIcon ? <SubIcon className={`h-3.5 w-3.5 flex-shrink-0 um-icon-tile ${isActiveSub ? "text-amber-300" : iconTint(c.accent)}`}/> : <span className="w-3.5"/>}
                  <span className="flex-1 text-left">{c.label}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </li>
  );
}

function SidebarBody({ activeTab, activeSub, onTopNav, adminRole, adminRoles, subsEnabled, onClose, unreadBadges }) {
  const tree = buildNavTree(adminRole, subsEnabled, adminRoles);
  // v2.7.0 batch DO+++++ · Pin-to-top — localStorage-backed array of value
  // strings. Items appearing here are rendered (cloned, w/o children) above
  // the main nav for one-tap access. Capped at 5 to keep the rail compact.
  const PIN_KEY = "rtg_admin_sidebar_pins";
  const PIN_CAP = 5;
  const [pinned, setPinned] = useState(() => {
    try { return JSON.parse(localStorage.getItem(PIN_KEY) || "[]"); } catch { return []; }
  });
  const togglePin = (val) => {
    setPinned((prev) => {
      let next;
      if (prev.includes(val)) {
        next = prev.filter((v) => v !== val);
      } else {
        if (prev.length >= PIN_CAP) {
          // Replace the oldest (FIFO) when at cap so the user can always pin
          // their latest selection without an extra "unpin first" step.
          next = [...prev.slice(1), val];
        } else {
          next = [...prev, val];
        }
      }
      try { localStorage.setItem(PIN_KEY, JSON.stringify(next)); } catch { /* no-op */ }
      return next;
    });
  };
  const pinnedItems = pinned
    .map((v) => tree.find((t) => t.value === v))
    .filter(Boolean);
  // Auto-expand the active parent on first render
  const [expanded, setExpanded] = useState(() => {
    const init = new Set();
    if (tree.find((i) => i.value === activeTab && i.children?.length)) init.add(activeTab);
    return init;
  });
  // When the parent tab switches via some other path, auto-expand its accordion.
  useEffect(() => {
    if (tree.find((i) => i.value === activeTab && i.children?.length)) {
      setExpanded((prev) => prev.has(activeTab) ? prev : new Set(prev).add(activeTab));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);
  const toggleExpanded = (v, forceExpand = false) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (forceExpand) { next.add(v); return next; }
      if (next.has(v)) next.delete(v); else next.add(v);
      return next;
    });
  };
  return (
    <nav data-testid="admin-sidebar-nav" className="space-y-1">
      <div className="flex items-center justify-between px-3 mb-2">
        <div className="text-[10px] uppercase tracking-widest text-white/50 font-bold">Admin Console</div>
        <VersionBadge inline/>
      </div>
      {/* v2.7.6 · live "who's watching dispatch" widget — backed by the same
          /api/admin/presence map that powers the roster dots. */}
      <div className="px-3 mb-3">
        <CurrentlyOnlineWidget/>
      </div>
      {pinnedItems.length > 0 && (
        <div className="mb-3 pb-2 border-b border-white/10" data-testid="sidebar-pinned-section">
          <div className="text-[9px] uppercase tracking-[0.2em] text-amber-300/80 px-3 mb-1 font-bold flex items-center gap-1">
            <Pin className="h-2.5 w-2.5 fill-amber-400"/>Pinned
          </div>
          <ul className="space-y-0.5">
            {pinnedItems.map((item) => (
              <NavRow
                key={`pin-${item.value}`}
                item={{ ...item, children: undefined }}
                activeTab={activeTab}
                activeSub={activeSub}
                onTopNav={onTopNav}
                expanded={false}
                toggleExpanded={() => {}}
                onClose={onClose}
                unreadBadges={unreadBadges}
                pinned={true}
                onTogglePin={togglePin}
              />
            ))}
          </ul>
        </div>
      )}
      <ul className="space-y-0.5">
        {tree.map((item, idx) => {
          // v2.7.0 batch DO++++ · FEATURE 2 · Render the "Taxi Commander Admin"
          // section header above the first item that declares `section`.
          const firstInSection = item.section && (idx === 0 || tree[idx - 1]?.section !== item.section);
          return (
            <Fragment key={item.value}>
              {firstInSection && (
                <li className="px-3 pt-4 pb-1.5 text-[10px] uppercase tracking-[0.18em] text-amber-300/80 font-bold border-t border-white/10 mt-2 list-none" data-testid={`sidebar-section-${item.section.toLowerCase().replace(/\s+/g, '-')}`}>
                  {item.section}
                </li>
              )}
              <NavRow
                item={item}
                activeTab={activeTab}
                activeSub={activeSub}
                onTopNav={onTopNav}
                expanded={expanded.has(item.value)}
                toggleExpanded={toggleExpanded}
                onClose={onClose}
                unreadBadges={unreadBadges}
                pinned={pinned.includes(item.value)}
                onTogglePin={togglePin}
              />
            </Fragment>
          );
        })}
      </ul>
    </nav>
  );
}

/**
 * AdminSidebar — desktop (≥md) renders the rail inline; mobile renders only
 * the hamburger button (the sticky rail is hidden via CSS).
 *
 * Props:
 *   activeTab    string · parent <Tabs value=…>
 *   onTopNav     fn(value)
 *   adminRole    "admin" | "supervisor" | "dispatcher"
 *   subsEnabled  bool · controls Subscriptions sub-tab visibility
 */
export default function AdminSidebar({ activeTab, onTopNav, adminRole, subsEnabled = true, unreadBadges: extraBadges = {} }) {
  const [open, setOpen] = useState(false);
  // v2.7.0 batch DO++ · FEATURE 1 · Fetch caller's full canonical roles[] so
  // the sidebar can show/hide the Developer menu group correctly even when
  // an Operating Partner is signed in (legacy adminRole='supervisor').
  const [adminRoles, setAdminRoles] = useState([]);
  useEffect(() => {
    const token = localStorage.getItem("rtg_admin_token");
    if (!token) return;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/me/roles`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.ok ? r.json() : null)
      .then((d) => { if (d?.roles) setAdminRoles(d.roles); })
      .catch(() => {});
  }, []);
  // Read current sub-tab from hash so the active highlight is right.
  const readSub = () => {
    const h = (window.location.hash || "").replace(/^#/, "");
    return new URLSearchParams(h).get("sub");
  };
  const [activeSub, setActiveSub] = useState(readSub());
  useEffect(() => {
    const onHash = () => setActiveSub(readSub());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  // v2.7.0 batch CW · Chat unread badge — polls every 20s + immediately
  // when the admin lands on the Chat tab (clears badge after open).
  const [unreadBadges, setUnreadBadges] = useState({ chatbot: 0 });
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const token = localStorage.getItem("rtg_admin_token");
        if (!token) return;
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/chatbot/unread-count`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setUnreadBadges({ chatbot: data.total || 0 });
      } catch (err) { /* network blip — try next poll */ void err; }
    };
    tick();
    const id = setInterval(tick, 20000);
    return () => { cancelled = true; clearInterval(id); };
  }, [activeTab]);

  return (
    <>
      {/* Mobile hamburger trigger — fixed top-left under the header strip */}
      <div className="md:hidden mb-3" data-testid="admin-sidebar-mobile-toggle-wrapper">
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              data-testid="admin-sidebar-mobile-toggle"
              className="bg-zinc-950 border-zinc-700 h-9"
            >
              <Menu className="h-4 w-4 mr-1.5"/>
              Menu
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="bg-slate-950 border-white/10 w-72 p-4 overflow-y-auto flex flex-col" data-testid="admin-sidebar-drawer">
            <div className="flex-1 min-h-0 overflow-y-auto">
              <SidebarBody
                activeTab={activeTab}
                activeSub={activeSub}
                onTopNav={onTopNav}
                adminRole={adminRole}
                adminRoles={adminRoles}
                subsEnabled={subsEnabled}
                onClose={() => setOpen(false)}
                unreadBadges={{ ...unreadBadges, ...extraBadges }}
              />
            </div>
            <SidebarProfileFooter/>
          </SheetContent>
        </Sheet>
      </div>

      {/* Desktop rail */}
      <aside
        className="hidden md:flex md:flex-col w-56 flex-shrink-0 bg-slate-950 border border-white/10 rounded-lg p-3 sticky top-4 self-start max-h-[calc(100vh-2rem)] overflow-hidden"
        data-testid="admin-sidebar"
      >
        <div className="flex-1 min-h-0 overflow-y-auto pr-1 -mr-1">
          <SidebarBody
            activeTab={activeTab}
            activeSub={activeSub}
            onTopNav={onTopNav}
            adminRole={adminRole}
            adminRoles={adminRoles}
            subsEnabled={subsEnabled}
            unreadBadges={{ ...unreadBadges, ...extraBadges }}
          />
        </div>
        <SidebarProfileFooter/>
      </aside>
    </>
  );
}
